import { Box, Typography, useTheme, useMediaQuery, CircularProgress, Button } from '@mui/material';
import { GoChevronLeft } from 'react-icons/go';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import FormFooter from './partials/FormFooter';
import {
  useGetAgencyOnboardingForm,
  useGetPublicOnboardingForm,
  useCreateOrUpdateAgencyOnboardingForm,
  useUpdatePublicOnboardingForm,
  useGetPublicAvailableIntegrations
} from '@/apis/agency/businessOnboarding/hooks';
import { OnboardingFormRequest, TransferCallDetails } from '@/apis/agency/businessOnboarding/types';
import Spinner from '@/components/Common/Spinner';
import { useToast } from '@/hooks/toast';
import OnboardingThankYou from './components/OnboardingThankYou';
import {
  OnboardingFormDefaultValues,
  getEmergencyDefaultMessage,
  shouldApplyDefaultAgentCapabilities
} from './components/constants';
import Stepper from './components/Stepper';
import {
  getStepFields,
  STEPS,
  stepHasError,
  ONBOARDING_COMPLETED,
  ONBOARDING_PROGRESS_CHANGED,
  getOverallFormProgress,
  getIntegrationStepIndex,
  getIntegrationCredentials,
  normalizeIntegrationConfiguration,
  stripOAuthTokensFromConfiguration,
  omitIntegrationValidationMeta
} from './components/utils';
import { getOfficeOpsNav, OFFICE_OPS_NAV_CHANGED } from './components/officeOpsNav';
import { zodResolver } from '@hookform/resolvers/zod';
import { onboardingSchema, publicOnboardingSchema } from '@/utils/schema/validator/OnboardingFormVaildation';
import FeaturebaseWidget from '@/components/Common/FeatureBasePopup';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';
import * as urls from 'routes/config';
import { normalizeWebsiteUrl } from '@/utils/helperFunctions';
import { getOauthConfiguration } from '@/apis/agency/businessOnboarding/apis';
import { useGetTelephonyProviders as useGetTelephonyProvidersProviders } from '@/apis/agency/providers/hook';
import { useGetTelephonyProviders as useGetTelephonyProvidersTelephony } from '@/apis/agency/telephony/hook';

export interface AgentReadinessFormValues {
  businessLegalName: string;
  websiteUrl: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  state: string;
  country: string;
}

export default function AgentReadinessForm({ isEditingView = false }: { isEditingView?: boolean }) {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [officeOpsCanGoPrev, setOfficeOpsCanGoPrev] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasInitializedStep = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBusinessOnboardingStage2 = location.pathname.includes('/business-onboarding/stage-2');
  const { showToast, ToastComponent } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitted, setIsSubmitted] = useState(searchParams.get('success') === 'true');
  const oauthCallbackHandled = useRef(false);
  const oauthErrorHandled = useRef(false);
  const onboardingId = Cookies.get('onboarding_id');
  const { onboardingFormId } = useParams<{ onboardingFormId?: string; formId?: string }>();
  const isPublicRoute = isBusinessOnboardingStage2;

  const { data: agencyOnboardingFormData, isLoading: isAgencyLoading } = useGetAgencyOnboardingForm({
    enabled: !isPublicRoute && Boolean(onboardingFormId)
  } as any);

  const {
    data: publicOnboardingFormData,
    isLoading: isPublicLoading,
    isFetching: isPublicFetching
  } = useGetPublicOnboardingForm(onboardingId || '', {
    enabled: isPublicRoute && Boolean(onboardingId)
  });

  const onboardingFormData = useMemo(
    () => (isPublicRoute ? publicOnboardingFormData : agencyOnboardingFormData),
    [isPublicRoute, publicOnboardingFormData, agencyOnboardingFormData]
  );
  const isLoading = isPublicRoute ? isPublicLoading : isAgencyLoading;

  const { data: providersData } = useGetTelephonyProvidersProviders(
    { filter: { active_eq: true } },
    { enabled: isPublicRoute }
  );

  const { data: telephonyData } = useGetTelephonyProvidersTelephony(
    { filter: { active_eq: true } },
    { enabled: !isPublicRoute }
  );

  const providers = isPublicRoute ? providersData : telephonyData;

  useEffect(() => {
    if (!isPublicRoute || !publicOnboardingFormData || isPublicFetching) return;
    const config = publicOnboardingFormData.onboarding_config;
    if (String(config?.stage) === '1') {
      navigate(`${urls.businessOnboarding.base}/${urls.businessOnboarding.stage1()}`, { replace: true });
    }
  }, [isPublicRoute, publicOnboardingFormData, navigate, isPublicFetching]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [activeStep]);

  useEffect(() => {
    if (hasInitializedStep.current || !onboardingFormData) return;
    if (isPublicRoute && isPublicFetching) return;

    const publicMax = STEPS(true).length - 1;

    if (isPublicRoute) {
      const config = onboardingFormData.onboarding_config;
      if (String(config?.stage) !== '2') return;

      const urlStep = searchParams.get('onboarding_step');
      const step = urlStep || config?.step;
      if (step) {
        setActiveStep(Math.min(Math.max(0, Number(step) - 1), publicMax));
      }
      hasInitializedStep.current = true;
    } else if (onboardingFormData.step_config?.current_step !== undefined) {
      const lastStep = STEPS(false).length - 1;
      const saved = Number(onboardingFormData.step_config.current_step);
      const step = Number.isFinite(saved) && saved >= 0 && saved <= lastStep ? saved : lastStep;
      setActiveStep(step);
      hasInitializedStep.current = true;
    }
  }, [onboardingFormData, isPublicRoute, isPublicFetching, searchParams]);

  // Use appropriate update hook based on route
  const { mutate: updateAgencyOnboardingForm, isPending: isAgencySubmitting } =
    useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublicOnboardingForm, isPending: isPublicSubmitting } = useUpdatePublicOnboardingForm();

  const updateOnboardingForm = isPublicRoute
    ? (payload: OnboardingFormRequest, options?: any) => {
        if (onboardingId) {
          updatePublicOnboardingForm({ formId: onboardingId, payload }, options);
        }
      }
    : updateAgencyOnboardingForm;

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const applyDefaultCapabilities = shouldApplyDefaultAgentCapabilities(onboardingFormData);

  const methods = useForm<any>({
    resolver: zodResolver(isPublicRoute ? publicOnboardingSchema : onboardingSchema),
    defaultValues: OnboardingFormDefaultValues,
    values: {
      ...OnboardingFormDefaultValues,
      ...onboardingFormData,
      status: onboardingFormData?.status || '',

      // ✅ Address with timezone fallback to browser timezone
      address: {
        ...onboardingFormData?.address,
        time_zone: onboardingFormData?.address?.time_zone || browserTimezone
      },

      onboarding_recordings_attributes: onboardingFormData?.onboarding_recordings_attributes?.length
        ? onboardingFormData.onboarding_recordings_attributes
        : [
            {
              file: null,
              description: ''
            }
          ],

      office_operations_files_attributes: onboardingFormData?.office_operations_files_attributes?.length
        ? onboardingFormData.office_operations_files_attributes
        : OnboardingFormDefaultValues.office_operations_files_attributes,

      // ✅ Member Details
      operator_details: onboardingFormData?.operator_details?.length
        ? onboardingFormData.operator_details
        : [
            {
              name: '',
              email: '',
              role: '',
              phone: '',
              main_line_number: '',
              extension: '',
              sip_uri: '',
              working_hours: {}
            }
          ],

      coupon_code: onboardingFormData?.coupon_code || '',
      stripe_customer_id: onboardingFormData?.stripe_customer_id || '',

      // ✅ Transfer Call Details (array of action-aligned configs)
      transfer_call_details: (() => {
        // API may still return a legacy single-object payload
        const raw = onboardingFormData?.transfer_call_details as
          | TransferCallDetails[]
          | TransferCallDetails
          | undefined;
        const defaults = OnboardingFormDefaultValues.transfer_call_details;
        if (Array.isArray(raw) && raw.length) {
          return raw.map((item) => ({
            ...defaults[0],
            ...item,
            sources: Array.isArray(item?.sources) && item.sources.length ? item.sources : defaults[0].sources
          }));
        }
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          return [
            {
              ...defaults[0],
              ...raw,
              sources: Array.isArray(raw.sources) && raw.sources.length ? raw.sources : defaults[0].sources
            }
          ];
        }
        return defaults;
      })(),

      // ✅ Default-enable Take Note / Spam / Emergency for early-stage forms
      note_taking_enable: applyDefaultCapabilities ? true : !!onboardingFormData?.note_taking_enable,
      spam_call_termination_enable: applyDefaultCapabilities
        ? true
        : !!onboardingFormData?.spam_call_termination_enable,
      emergency_call_handling_enable: applyDefaultCapabilities
        ? true
        : !!onboardingFormData?.emergency_call_handling_enable,
      emergency_call_handling_details: {
        mode: 'message',
        ...onboardingFormData?.emergency_call_handling_details,
        message:
          onboardingFormData?.emergency_call_handling_details?.message ||
          getEmergencyDefaultMessage(onboardingFormData?.industry)
      },

      // ✅ Spam Patterns (NEW)
      spam_call_termination_details: {
        ...onboardingFormData?.spam_call_termination_details,

        patterns: onboardingFormData?.spam_call_termination_details?.patterns?.length
          ? onboardingFormData.spam_call_termination_details.patterns
          : [{ value: '' }]
      },

      // ✅ Deep merge location_details to preserve defaults and prefill from address
      location_details: {
        ...OnboardingFormDefaultValues.location_details,
        ...onboardingFormData?.location_details,
        street: onboardingFormData?.location_details?.street || onboardingFormData?.address?.street_address || '',
        city: onboardingFormData?.location_details?.city || onboardingFormData?.address?.city || '',
        state: onboardingFormData?.location_details?.state || onboardingFormData?.address?.state_province_region || '',
        zip: onboardingFormData?.location_details?.zip || onboardingFormData?.address?.postal_zip_code || '',
        country: onboardingFormData?.location_details?.country || onboardingFormData?.address?.country || ''
      },

      // ✅ Ensure IDs are strings for Select matching and validation
      business_plan_id: onboardingFormData?.business_plan_id ? String(onboardingFormData.business_plan_id) : '',
      voice_engine_id: onboardingFormData?.voice_engine_id ? String(onboardingFormData.voice_engine_id) : '',

      // ✅ Map Integration Name back to ID if needed (for Other/No Integration)
      available_integration_ids: onboardingFormData?.available_integration_ids?.length
        ? onboardingFormData.available_integration_ids
        : onboardingFormData?.available_integration_name === 'other'
          ? ['other']
          : onboardingFormData?.available_integration_name === 'No Integration'
            ? [' ']
            : [],

      other_integration_name: onboardingFormData?.other_integration_name || '',

      telephony_provider_ids: Array.isArray(onboardingFormData?.telephony_provider_ids)
        ? onboardingFormData.telephony_provider_ids.map((id: number | string) => String(id))
        : []
    },
    resetOptions: {
      keepDirtyValues: true
    }
  });

  const isUploadingDoc = methods.watch('isUploadingDoc');
  const businessLegalName = methods.watch('business_legal_name');
  const isSubmitting = (isPublicRoute ? isPublicSubmitting : isAgencySubmitting) || isUploadingDoc;
  const formValues = methods.watch();

  const selectedIndustry = methods.watch('industry');
  const { data: availableIntegrations } = useGetPublicAvailableIntegrations(
    {
      per_page: 999,
      filter: { industries_contain: selectedIndustry }
    },
    { enabled: !!selectedIndustry }
  );
  const integrationsList = useMemo(() => availableIntegrations?.data || [], [availableIntegrations]);

  const overallProgressData = useMemo(() => {
    return getOverallFormProgress(formValues, isPublicRoute, integrationsList);
  }, [formValues, isPublicRoute, integrationsList]);

  const overallProgress = overallProgressData.percentage;
  const stepProgresses = overallProgressData.stepProgresses;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(ONBOARDING_PROGRESS_CHANGED, { detail: overallProgress }));
    window.dispatchEvent(new CustomEvent('ONBOARDING_STEPS_PROGRESS_CHANGED', { detail: stepProgresses }));
  }, [overallProgress, stepProgresses]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ONBOARDING_STEP_CHANGED', { detail: activeStep }));
  }, [activeStep]);

  useEffect(() => {
    const handleNavigate = (event: any) => {
      setActiveStep(event.detail);
    };
    window.addEventListener('ONBOARDING_NAVIGATE_STEP', handleNavigate);
    return () => {
      window.removeEventListener('ONBOARDING_NAVIGATE_STEP', handleNavigate);
    };
  }, []);

  useEffect(() => {
    const handleOfficeOpsNav = (event: Event) => {
      const detail = (event as CustomEvent<{ canGoPrev?: boolean }>).detail;
      setOfficeOpsCanGoPrev(Boolean(detail?.canGoPrev));
    };
    window.addEventListener(OFFICE_OPS_NAV_CHANGED, handleOfficeOpsNav);
    return () => {
      window.removeEventListener(OFFICE_OPS_NAV_CHANGED, handleOfficeOpsNav);
      setOfficeOpsCanGoPrev(false);
    };
  }, [activeStep]);

  const preparePayload = (formData: any) => {
    const payload = { ...formData };
    const integrationId = payload.available_integration_ids?.[0];

    if (payload.website_url) {
      payload.website_url = normalizeWebsiteUrl(payload.website_url);
    }

    if (integrationId === ' ' || integrationId === 'other') {
      payload.available_integration_name = integrationId === ' ' ? 'No Integration' : 'other';
      payload.available_integration_ids = [];
    }

    const rawTelephony = Array.isArray(payload.telephony_provider_ids) ? payload.telephony_provider_ids : [];
    const telephonyIds = new Set<number>();

    rawTelephony
      .map((id: string | number) => (typeof id === 'string' ? parseInt(id, 10) : Number(id)))
      .filter((id: number) => Number.isFinite(id) && id > 0)
      .forEach((id: number) => telephonyIds.add(id));

    const voipProviderCode = payload.current_business_solution?.telephony_voip_provider;
    if (voipProviderCode && voipProviderCode !== 'Other' && providers?.data) {
      const matchedProvider = providers.data.find((p: any) => p.code === voipProviderCode);
      if (matchedProvider?.id) {
        telephonyIds.add(Number(matchedProvider.id));
      }
    }

    payload.telephony_provider_ids = Array.from(telephonyIds);

    return payload;
  };

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (!oauthError || oauthErrorHandled.current || !onboardingFormData) return;

    const integrationStepIndex = getIntegrationStepIndex(isPublicRoute);
    if (integrationStepIndex < 0) return;

    oauthErrorHandled.current = true;
    setActiveStep(integrationStepIndex);

    const existing = ((onboardingFormData as any)?.integration_configuration || {}) as Record<string, unknown>;
    const strippedCredentials = stripOAuthTokensFromConfiguration(getIntegrationCredentials(existing));
    const failedConfiguration = normalizeIntegrationConfiguration(existing, {
      configuration: strippedCredentials,
      valid: false,
      valid_creds: false,
      validation_message: oauthError
    });

    methods.setValue('integration_configuration', failedConfiguration, { shouldDirty: true });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('oauth_error');
    setSearchParams(nextParams, { replace: true });

    const formData = preparePayload({
      ...methods.getValues(),
      integration_configuration: failedConfiguration
    });
    if (formData.status === '') {
      delete formData.status;
    }

    updateOnboardingForm({
      onboarding_form: {
        ...formData,
        integration_configuration: failedConfiguration,
        ...(isPublicRoute
          ? {
              onboarding_config: {
                ...(onboardingFormData?.onboarding_config || {}),
                step: String(integrationStepIndex + 1),
                stage: '2',
                overall_progress: overallProgress
              }
            }
          : {
              step_config: {
                ...(onboardingFormData?.step_config || {}),
                current_step: integrationStepIndex,
                overall_progress: overallProgress
              }
            })
      },
      update_message: 'Integration validation failed'
    });

    showToast(oauthError, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isPublicRoute, onboardingFormData]);

  useEffect(() => {
    const token = searchParams.get('oauth_configuration_token');
    if (!token || oauthCallbackHandled.current || !onboardingFormData) return;

    const integrationStepIndex = getIntegrationStepIndex(isPublicRoute);
    if (integrationStepIndex < 0) return;

    const selectedIntegrationId = methods.getValues('available_integration_ids')?.[0];
    const selectedIntegration = integrationsList.find(
      (integration: any) => String(integration.id) === String(selectedIntegrationId)
    );
    if (!selectedIntegration?.integration_name) return;

    oauthCallbackHandled.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('oauth_configuration_token');
    setSearchParams(nextParams, { replace: true });

    getOauthConfiguration(token)
      .then(async ({ configuration }) => {
        const existing = (methods.getValues('integration_configuration') || {}) as Record<string, unknown>;
        const oauthPayload =
          configuration && typeof configuration === 'object' && !Array.isArray(configuration)
            ? (configuration as Record<string, unknown>)
            : {};
        const isValid = oauthPayload.valid === true;
        const validationMessage =
          typeof oauthPayload.validation_message === 'string' ? oauthPayload.validation_message : undefined;
        const oauthCredentials = omitIntegrationValidationMeta(oauthPayload);
        const mergedCredentials = {
          ...getIntegrationCredentials(existing),
          ...oauthCredentials
        };
        const mergedConfiguration = normalizeIntegrationConfiguration(existing, {
          configuration: mergedCredentials,
          valid: isValid,
          valid_creds: isValid,
          validation_message: isValid ? null : validationMessage
        });

        methods.setValue('integration_configuration', mergedConfiguration, { shouldDirty: true });
        setActiveStep(integrationStepIndex);

        const formData = preparePayload({
          ...methods.getValues(),
          integration_configuration: mergedConfiguration
        });

        if (formData.status === '') {
          delete formData.status;
        }

        updateOnboardingForm({
          onboarding_form: {
            ...formData,
            integration_configuration: mergedConfiguration,
            ...(isPublicRoute
              ? {
                  onboarding_config: {
                    ...(onboardingFormData?.onboarding_config || {}),
                    step: String(integrationStepIndex + 1),
                    stage: '2',
                    overall_progress: overallProgress
                  }
                }
              : {
                  step_config: {
                    ...(onboardingFormData?.step_config || {}),
                    current_step: integrationStepIndex,
                    overall_progress: overallProgress
                  }
                })
          },
          update_message: isValid ? 'Integration credentials saved successfully' : 'Integration validation failed'
        });

        if (isValid) {
          showToast('Integration connected successfully', 'success');
        } else {
          showToast(
            validationMessage || 'Credentials saved, but validation failed. You can retry from the integration step.',
            'error'
          );
        }
      })
      .catch(() => {
        showToast('Failed to save OAuth credentials. Please try again.', 'error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, onboardingFormData, integrationsList, isPublicRoute]);

  const { data: organizationData } = useGetOrganizations();

  const handleSaveDraft = (targetStep?: number) => {
    const formData = preparePayload(methods.getValues());
    const stepLabel = STEPS(isPublicRoute)[activeStep]?.label;

    if (formData.status === '') {
      delete formData.status;
    }

    updateOnboardingForm({
      onboarding_form: {
        ...formData,
        acknowledge_sign: formData.confirmation || false,
        ...(isPublicRoute
          ? {
              onboarding_config: {
                ...(onboardingFormData?.onboarding_config || {}),
                step: String((targetStep ?? activeStep) + 1),
                stage: '2',
                overall_progress: overallProgress
              }
            }
          : {
              step_config: {
                ...(onboardingFormData?.step_config || {}),
                current_step: targetStep ?? activeStep,
                overall_progress: overallProgress
              }
            }),
        status: formData.status
      },
      update_message: `${stepLabel} updated successfully`
    });
  };

  const handleSubmitReview = () => {
    const formData = preparePayload(methods.getValues());

    if (formData.status === '') {
      delete formData.status;
    }

    updateOnboardingForm(
      {
        onboarding_form: {
          ...formData,
          acknowledge_sign: formData.confirmation || false,
          ...(isPublicRoute
            ? {
                onboarding_config: {
                  ...(onboardingFormData?.onboarding_config || {}),
                  step: String(STEPS(isPublicRoute).length),
                  stage: '2',
                  overall_progress: overallProgress
                }
              }
            : {
                step_config: {
                  ...(onboardingFormData?.step_config || {}),
                  current_step: STEPS(isPublicRoute).length - 1,
                  overall_progress: overallProgress
                }
              })
        }
      },
      {
        onSuccess: () => {
          showToast('Form submitted for review successfully', 'success');
          if (isPublicRoute) {
            setIsSubmitted(true);
            // Clear onboarding related cookies after success
            const clearOnboardingCookies = () => {
              const keys = [
                'onboarding_id',
                'onboard-token',
                'onboarding_data',
                'onboarding_customer_id',
                'onboarding_referrer',
                'onboarding_program_id',
                'onboarding_partner_id',
                'auth_token'
              ];
              keys.forEach((k) => Cookies.remove(k));
            };
            clearOnboardingCookies();
            navigate(`${location.pathname}?success=true`, { replace: true });
            window.dispatchEvent(new CustomEvent(ONBOARDING_COMPLETED, { detail: true }));
          } else {
            navigate(-1);
          }
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to submit form';
          showToast(errorMessage, 'error');
        }
      }
    );
  };

  if (isLoading && !onboardingFormData) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={400}>
        <Spinner />
      </Box>
    );
  }

  if (isSubmitted && isPublicRoute) {
    return <OnboardingThankYou />;
  }

  const isProduction = import.meta.env.VITE_APP_ENV === 'production';
  const shouldShowFeaturebase =
    isProduction && organizationData?.uid === 'AG316997' && isBusinessOnboardingStage2 && !isSubmitted;

  return (
    <FormProvider {...methods}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          component='form'
          onSubmit={(e) => e.preventDefault()}
          // onBlurCapture={handleAutoSave}
          display='flex'
          flexDirection='column'
          gap={3}
          pb={2}
          pt={{ md: isBusinessOnboardingStage2 ? 3 : 0 }}
          paddingX={{ xs: 2, lg: isBusinessOnboardingStage2 ? 9 : 3 }}
          mt={{ xs: isBusinessOnboardingStage2 ? '0px' : '-16px', lg: 0 }}
          sx={{ flex: 1, overflow: 'hidden' }}
        >
          {/* Desktop Header */}
          {isEditingView && !isMobile && (
            <Box display='flex' alignItems='start' gap={2}>
              <Box
                onClick={() => navigate(-1)}
                display='flex'
                alignItems='center'
                justifyContent='center'
                border={`1px solid ${theme.palette.primary.light}`}
                borderRadius='50%'
                mt={0.6}
                width={22}
                height={22}
                sx={{ cursor: 'pointer', bgcolor: '#fff' }}
              >
                <GoChevronLeft color={theme.palette.primary.main} />
              </Box>

              <Box>
                <Typography variant='h6' fontWeight={500}>
                  {businessLegalName
                    ? `${businessLegalName} - Agent Readiness Prerequisites`
                    : 'Agent Readiness Prerequisites'}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Complete all business and agent details to configure your AI agent
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mobile Stepper Header */}
          {isMobile && (
            <Stepper
              isMobile={true}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              isPublicRoute={isPublicRoute}
              overallProgress={overallProgress}
              stepProgresses={stepProgresses}
            />
          )}

          {/* Form */}
          <Box display='flex' gap={3} flex={1} overflow='hidden'>
            {!isMobile && (
              <Box width='23%' sx={{ height: '100%', overflowY: 'auto' }} paddingRight={2}>
                <Stepper activeStep={activeStep} setActiveStep={setActiveStep} isPublicRoute={isPublicRoute} />
              </Box>
            )}

            <Box display='flex' flexDirection='column' width={isMobile ? '100%' : '75%'} sx={{ height: '100%' }}>
              <Box ref={scrollRef} flex={1} overflow='auto' pr={isMobile ? 0 : 2}>
                {React.createElement(STEPS(isPublicRoute)[activeStep]?.component, { activeStep })}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            bgcolor: '#fff',
            borderTop: '1px solid #eee',
            zIndex: 100
          }}
        >
          {isMobile ? (
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Button
                variant='outlined'
                size='small'
                color='secondary'
                disabled={activeStep === 0 && !officeOpsCanGoPrev}
                onClick={() => {
                  const officeOpsNav = getOfficeOpsNav();
                  if (officeOpsNav?.canGoPrev()) {
                    officeOpsNav.goPrev();
                    return;
                  }
                  if (activeStep > 0) setActiveStep((prev) => prev - 1);
                }}
                sx={{
                  minWidth: 80,
                  borderRadius: 20,
                  visibility: activeStep === 0 && !officeOpsCanGoPrev ? 'hidden' : 'visible'
                }}
              >
                Back
              </Button>

              <Typography
                sx={{
                  fontSize: '13px',
                  color: 'text.secondary',
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}
              >
                Step {activeStep + 1} of {STEPS(isPublicRoute).length}
              </Typography>

              <Button
                variant='contained'
                size='small'
                sx={{ borderRadius: 20, minWidth: 80 }}
                disabled={isSubmitting}
                onClick={async () => {
                  const officeOpsNav = getOfficeOpsNav();
                  if (officeOpsNav?.canGoNext()) {
                    officeOpsNav.goNext();
                    return;
                  }
                  if (activeStep === STEPS(isPublicRoute).length - 1) {
                    methods.handleSubmit(() => handleSubmitReview())();
                  } else {
                    const values = methods.getValues();
                    const fieldsToValidate = getStepFields(activeStep, values, isPublicRoute);
                    const isValid = await methods.trigger(fieldsToValidate);
                    if (isValid) {
                      handleSaveDraft(activeStep + 1);
                      setActiveStep((prev) => prev + 1);
                    }
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={16} color='inherit' />
                ) : activeStep === STEPS(isPublicRoute).length - 1 ? (
                  isPublicRoute ? (
                    'Submit'
                  ) : (
                    'Save'
                  )
                ) : (
                  'Continue'
                )}
              </Button>
            </Box>
          ) : (
            <FormFooter
              onSaveDraft={async () => {
                const officeOpsNav = getOfficeOpsNav();
                if (officeOpsNav?.canGoNext()) {
                  officeOpsNav.goNext();
                  return;
                }

                const values = methods.getValues();

                const fieldsToValidate = getStepFields(activeStep, values, isPublicRoute);

                const isValid = await methods.trigger(fieldsToValidate);

                if (!isValid) return;

                const nextStep = activeStep + 1;
                handleSaveDraft(nextStep);

                if (activeStep < STEPS(isPublicRoute).length - 1) {
                  setActiveStep(nextStep);
                }
              }}
              onSubmitReview={methods.handleSubmit(
                () => {
                  handleSubmitReview();
                },
                (errors) => {
                  const steps = STEPS(isPublicRoute);
                  const errorSteps = steps
                    .map((step, index) => (stepHasError(index, errors, isPublicRoute) ? step.label : null))
                    .filter(Boolean);

                  if (errorSteps.length > 0) {
                    showToast(`Please complete the following sections: ${errorSteps.join(', ')}`, 'error');
                  } else {
                    showToast('Please check the form for errors', 'error');
                  }
                }
              )}
              isSubmitting={isSubmitting}
              mode={isEditingView ? 'edit' : 'public'}
              showBack={activeStep > 0 || officeOpsCanGoPrev}
              onBack={() => {
                const officeOpsNav = getOfficeOpsNav();
                if (officeOpsNav?.canGoPrev()) {
                  officeOpsNav.goPrev();
                  return;
                }
                if (activeStep > 0) setActiveStep((prev) => prev - 1);
              }}
              isLastStep={activeStep === STEPS(isPublicRoute).length - 1}
              isPublicRoute={isPublicRoute}
            />
          )}
        </Box>
        {ToastComponent}
        {shouldShowFeaturebase && <FeaturebaseWidget />}
      </Box>
    </FormProvider>
  );
}
